import {Owner} from 'auth/PermissionConstants';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {DEFAULT_DURATION} from 'nav/toasts/ToastConstants';
import notifications, {Response} from 'notifications';
import {Thunk} from 'redux-stores/Store';
import {ClientSchemaField} from 'registry/field/FieldTable';
import getSpecError, {ErrorState} from 'ui-library/spec-editor/getSpecError';

import {SchemaSpec} from './SchemaConfigConstants';
import {getSchemaOwnerChoices} from './SchemaSpecUtil';

export enum SchemaConfigActionType {
  SCHEMA_SPEC_ALLOW_CREATE_RUN_FROM_NLAD_CHANGED = 'SCHEMA_SPEC_ALLOW_CREATE_RUN_FROM_NLAD_CHANGED',
  SCHEMA_SPEC_ALLOW_INSERT_RUN_FROM_INBOX_CHANGED = 'SCHEMA_SPEC_ALLOW_INSERT_RUN_FROM_INBOX_CHANGED',
  SCHEMA_SPEC_SHOULD_RECORD_EQUIPMENT_CHANGED = 'SCHEMA_SPEC_SHOULD_RECORD_EQUIPMENT_CHANGED',
  SCHEMA_SPEC_CHANGED = 'SCHEMA_SPEC_CHANGED',
  SCHEMA_SPEC_ADAPTER_ID_CHANGED = 'SCHEMA_SPEC_ADAPTER_ID_CHANGED',
  SCHEMA_SPEC_FETCHED = 'SCHEMA_SPEC_FETCHED',
  SCHEMA_SPEC_RESET_TO_DRAFT = 'SCHEMA_SPEC_RESET_TO_DRAFT',
  SCHEMA_SPEC_UPDATE_FAILED = 'SCHEMA_SPEC_UPDATE_FAILED',
  SCHEMA_SPEC_INPUT_FILE_CONFIG_CHANGED = 'SCHEMA_SPEC_INPUT_FILE_CONFIG_CHANGED',
  SCHEMA_SPEC_OUTPUT_FILE_CONFIG_CHANGED = 'SCHEMA_SPEC_OUTPUT_FILE_CONFIG_CHANGED',
  SCHEMA_SPEC_FIELD_CHANGED = 'SCHEMA_SPEC_FIELD_CHANGED',
  SCHEMA_SPEC_CREATE_STATE_CHANGE = 'SCHEMA_SPEC_CREATE_STATE_CHANGE',
  SCHEMA_SPEC_OWNER_CHANGED = 'SCHEMA_SPEC_OWNER_CHANGED',
  SCHEMA_SPEC_NAME_CHANGED = 'SCHEMA_SPEC_NAME_CHANGED',
  SCHEMA_SPEC_SQL_IDENTIFIER_CHANGED = 'SCHEMA_SPEC_SQL_IDENTIFIER_CHANGED',
  SCHEMA_SPEC_UI_RESET = 'SCHEMA_SPEC_UI_RESET',
}

export interface SchemaSpecChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_CHANGED;
  specString: string;
}

export interface SchemaSpecAdapterIdChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_ADAPTER_ID_CHANGED;
  adapterId: string | null;
}

export interface SchemaSpecFetchedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_FETCHED;
  schemaId: string | null;
  spec: SchemaSpec;
}

export interface SchemaSpecResetToDraftAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_RESET_TO_DRAFT;
  specString: string;
}

export interface SchemaSpecFailureAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_UPDATE_FAILED;
  errorState: ErrorState;
}

export interface SchemaSpecInputFileConfigChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_INPUT_FILE_CONFIG_CHANGED;
  inputFileConfigs: Array<AutomationInputFileConfigSpec>;
}

export interface SchemaSpecOutputFileConfigChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_OUTPUT_FILE_CONFIG_CHANGED;
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>;
}

export interface SchemaSpecFieldChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_FIELD_CHANGED;
  fields: Array<ClientSchemaField>;
}

export interface SchemaSpecUiResetAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_UI_RESET;
  ownerChoices: Array<Owner>;
  ownerId: string | null;
}

export interface SchemaSpecOwnerChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_OWNER_CHANGED;
  ownerId: string;
}

export interface SchemaSpecNameChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_NAME_CHANGED;
  name: string;
}

export interface SchemaSpecSqlIdentifierChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_SQL_IDENTIFIER_CHANGED;
  sqlIdentifier: string;
}

export interface SchemaSpecAllowCreateRunFromNLADChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_ALLOW_CREATE_RUN_FROM_NLAD_CHANGED;
  allowCreateRunFromNotebook: boolean;
}

export interface SchemaSpecAllowInsertRunFromInboxChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_ALLOW_INSERT_RUN_FROM_INBOX_CHANGED;
  allowInsertRunFromInbox: boolean;
}

export interface SchemaSpecShouldRecordEquipmentChangedAction {
  type: SchemaConfigActionType.SCHEMA_SPEC_SHOULD_RECORD_EQUIPMENT_CHANGED;
  shouldRecordEquipment: boolean;
}

export function schemaSpecResetToDraft(specString: string): SchemaSpecResetToDraftAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_RESET_TO_DRAFT,
    specString,
  };
}

export function schemaSpecChanged(specString: string): SchemaSpecChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_CHANGED,
    specString,
  };
}

export function schemaSpecFetched(schemaId: string | null, spec: SchemaSpec): SchemaSpecFetchedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_FETCHED,
    schemaId,
    spec,
  };
}

export function setSchemaSpecError(errorState: ErrorState): SchemaSpecFailureAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_UPDATE_FAILED,
    errorState,
  };
}

export function setSchemaInputFileConfig(
  inputFileConfigs: Array<AutomationInputFileConfigSpec>
): SchemaSpecInputFileConfigChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_INPUT_FILE_CONFIG_CHANGED,
    inputFileConfigs,
  };
}

export function setSchemaOutputFileConfig(
  outputFileConfigs: Array<ClientAutomationOutputFileConfigSpec>
): SchemaSpecOutputFileConfigChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_OUTPUT_FILE_CONFIG_CHANGED,
    outputFileConfigs,
  };
}

export function setSchemaSpecOwner(ownerId: string): SchemaSpecOwnerChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_OWNER_CHANGED,
    ownerId,
  };
}

export function setSchemaSpecFields(fields: Array<ClientSchemaField>): SchemaSpecFieldChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_FIELD_CHANGED,
    fields,
  };
}

export function setSchemaSpecName(name: string): SchemaSpecNameChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_NAME_CHANGED,
    name,
  };
}

export function setSchemaSpecSqlIdentifier(sqlIdentifier: string): SchemaSpecSqlIdentifierChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_SQL_IDENTIFIER_CHANGED,
    sqlIdentifier,
  };
}

export function setSchemaSpecAllowCreateRunFromNLAD(
  allowCreateRunFromNotebook: boolean
): SchemaSpecAllowCreateRunFromNLADChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_ALLOW_CREATE_RUN_FROM_NLAD_CHANGED,
    allowCreateRunFromNotebook,
  };
}

export function setSchemaSpecAllowInsertRunFromInbox(
  allowInsertRunFromInbox: boolean
): SchemaSpecAllowInsertRunFromInboxChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_ALLOW_INSERT_RUN_FROM_INBOX_CHANGED,
    allowInsertRunFromInbox,
  };
}

export function setSchemaSpecShouldRecordEquipment(
  shouldRecordEquipment: boolean
): SchemaSpecShouldRecordEquipmentChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_SHOULD_RECORD_EQUIPMENT_CHANGED,
    shouldRecordEquipment,
  };
}

export function setAdapterId(adapterId: string | null): SchemaSpecAdapterIdChangedAction {
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_ADAPTER_ID_CHANGED,
    adapterId,
  };
}

export function resetSchemaSpecUi(): SchemaSpecUiResetAction {
  const ownerChoices = getSchemaOwnerChoices();
  return {
    type: SchemaConfigActionType.SCHEMA_SPEC_UI_RESET,
    ownerChoices,
    ownerId: ownerChoices.length > 0 ? ownerChoices[0].id : null,
  };
}

export function handleSchemaSpecError(response: Response): Thunk<void> {
  return (dispatch) => {
    const errorState: ErrorState | null = getSpecError(response);
    if (errorState != null) {
      dispatch(setSchemaSpecError(errorState));
      notifications.notifyError(response, {duration: DEFAULT_DURATION});
    } else {
      notifications.notifyError(response);
    }
  };
}

export type SchemaConfigAction =
  | SchemaSpecAllowCreateRunFromNLADChangedAction
  | SchemaSpecAllowInsertRunFromInboxChangedAction
  | SchemaSpecShouldRecordEquipmentChangedAction
  | SchemaSpecChangedAction
  | SchemaSpecAdapterIdChangedAction
  | SchemaSpecFetchedAction
  | SchemaSpecResetToDraftAction
  | SchemaSpecFailureAction
  | SchemaSpecInputFileConfigChangedAction
  | SchemaSpecOutputFileConfigChangedAction
  | SchemaSpecOwnerChangedAction
  | SchemaSpecNameChangedAction
  | SchemaSpecSqlIdentifierChangedAction
  | SchemaSpecUiResetAction
  | SchemaSpecFieldChangedAction;
